import { useState, useEffect, useRef } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { RefreshCw, Trash2, Bell, BellOff, Save, Loader2, Slack, X, Plus } from 'lucide-react';
import type { Project, ProjectMember, EntityUser } from '../../types';
import type { SessionConfig } from '../../types';
import {
  updateProjectBriefSettings,
  getPersonalBriefPref,
  updatePersonalBriefPref,
  updateProject,
  getProjectMembers,
  addProjectMember,
  removeProjectMember,
  listUsers,
} from '../../lib/api';
import { queryKeys } from '../../lib/queryKeys';
import { track } from '../../lib/telemetry';
import { ConfirmModal } from '../ConfirmModal';

interface ProjectSettingsPanelProps {
  project: Project;
  sessionConfig: SessionConfig | null;
  canRefresh: boolean;
  canDelete: boolean;
  isRefreshing: boolean;
  onRefresh: () => void;
  onDeleteRequest: () => void;
}

// ── Section wrapper ───────────────────────────────────────────────────────────

function Section({ title, description, children }: {
  title: string;
  description?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="bg-white dark:bg-white/[0.03] rounded-2xl border border-gray-200 dark:border-white/[0.08] p-6">
      <div className="mb-4">
        <h3 className="text-title text-gray-900 dark:text-white">{title}</h3>
        {description && <p className="text-meta text-gray-500 dark:text-gray-400 mt-0.5">{description}</p>}
      </div>
      {children}
    </div>
  );
}

// ── Morning Brief (channel-level) ─────────────────────────────────────────────

function ChannelBriefSettings({ project, canEdit }: { project: Project; canEdit: boolean }) {
  const qc = useQueryClient();
  const [briefEnabled, setBriefEnabled] = useState(project.morningBriefEnabled ?? false);
  const [detailLevel, setDetailLevel] = useState<'summary' | 'detailed'>(project.briefDetailLevel ?? 'summary');
  const [saved, setSaved] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const briefEnabledRef = useRef(briefEnabled);
  const detailLevelRef = useRef(detailLevel);

  const mutation = useMutation({
    mutationFn: () => {
      const changedFields: string[] = [];
      if (briefEnabled !== (project.morningBriefEnabled ?? false)) changedFields.push('morningBriefEnabled');
      if (detailLevel !== (project.briefDetailLevel ?? 'summary')) changedFields.push('briefDetailLevel');
      track('project.settings.saved', 'action', { projectId: project.projectId, changedFields: changedFields.join(',') });
      return updateProjectBriefSettings(project.projectId, {
        morningBriefEnabled: briefEnabled,
        briefDetailLevel: detailLevel,
      });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.project(project.projectId) });
      briefEnabledRef.current = briefEnabled;
      detailLevelRef.current = detailLevel;
      setSaved(true);
      setSaveError(null);
      setTimeout(() => setSaved(false), 2500);
    },
    onError: (err: Error) => {
      setSaveError(err.message ?? 'Failed to save. Please try again.');
    },
  });

  const dirty =
    briefEnabled !== (project.morningBriefEnabled ?? false) ||
    detailLevel !== (project.briefDetailLevel ?? 'summary');

  return (
    <Section
      title="Channel Morning Brief"
      description="Post a project status brief to linked Slack channels at 9 AM company timezone. Mentions @channel to get team attention."
    >
      <div className="space-y-4">
        {/* Toggle */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <Slack className="h-4 w-4 text-gray-400" />
            <div>
              <p className="text-body font-medium text-gray-700 dark:text-gray-300">Daily Slack Brief</p>
              <p className="text-meta text-gray-400">Sent to all linked Slack channels</p>
            </div>
          </div>
          <button
            onClick={() => {
              if (!canEdit) return;
              const next = !briefEnabled;
              setBriefEnabled(next);
              track('project.brief_toggled', 'action', { projectId: project.projectId, enabled: next });
            }}
            disabled={!canEdit}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
              briefEnabled ? 'bg-primary' : 'bg-gray-200 dark:bg-white/20'
            } disabled:opacity-40`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                briefEnabled ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
        </div>

        {/* Detail level */}
        {briefEnabled && (
          <div>
            <label className="block text-meta font-medium text-gray-600 dark:text-gray-400 mb-1.5">
              Detail Level
            </label>
            <select
              value={detailLevel}
              onChange={(e) => canEdit && setDetailLevel(e.target.value as 'summary' | 'detailed')}
              disabled={!canEdit}
              className="w-full px-3 py-2 rounded-lg bg-white dark:bg-gray-900 border border-gray-200 dark:border-white/10 text-gray-900 dark:text-white text-meta focus:outline-none focus:border-primary/50 disabled:opacity-50"
            >
              <option value="summary">Summary — concise overview, top signals</option>
              <option value="detailed">Detailed — full facet breakdown, more context</option>
            </select>
          </div>
        )}

        {canEdit && dirty && (
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => mutation.mutate()}
            disabled={mutation.isPending}
            className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-xl text-meta font-medium hover:bg-primary/90 transition-colors disabled:opacity-50"
          >
            {mutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            {mutation.isPending ? 'Saving…' : 'Save'}
          </motion.button>
        )}

        {saved && (
          <p className="text-meta text-emerald-600 dark:text-emerald-400">Brief settings saved.</p>
        )}
        {saveError && (
          <p className="text-meta text-red-500">{saveError}</p>
        )}

        {!canEdit && (
          <p className="text-meta text-gray-400">Only PMs and admins can change brief settings.</p>
        )}
      </div>
    </Section>
  );
}

// ── Personal Brief (per-user opt-in) ─────────────────────────────────────────

function PersonalBriefSettings({ project }: { project: Project }) {
  const queryKey = ['personal-brief', project.projectId];
  const { data: enabled, isLoading } = useQuery({
    queryKey,
    queryFn: () => getPersonalBriefPref(project.projectId),
  });

  const qc = useQueryClient();
  const [briefError, setBriefError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: (val: boolean) => updatePersonalBriefPref(project.projectId, val),
    onMutate: async (val) => {
      await qc.cancelQueries({ queryKey });
      const previous = qc.getQueryData<boolean>(queryKey);
      qc.setQueryData(queryKey, val);
      setBriefError(null);
      return { previous };
    },
    onError: (err: Error, _val, ctx) => {
      qc.setQueryData(queryKey, ctx?.previous);
      setBriefError(err.message ?? 'Failed to update preference.');
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey });
    },
  });

  const current = enabled ?? false;

  return (
    <Section
      title="Personal Morning Brief"
      description="Receive a role-tailored Slack DM at 9 AM with what matters most to your role on this project."
    >
      {isLoading ? (
        <div className="skeleton h-8 rounded-lg w-32" />
      ) : (
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            {current ? (
              <Bell className="h-4 w-4 text-primary" />
            ) : (
              <BellOff className="h-4 w-4 text-gray-400" />
            )}
            <div>
              <p className="text-body font-medium text-gray-700 dark:text-gray-300">
                {current ? "Opted in \u2014 you'll receive a daily DM" : 'Not opted in'}
              </p>
              <p className="text-meta text-gray-400">Sent via Slack DM based on your role</p>
            </div>
          </div>
          <button
            onClick={() => mutation.mutate(!current)}
            disabled={mutation.isPending}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
              current ? 'bg-primary' : 'bg-gray-200 dark:bg-white/20'
            } disabled:opacity-40`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                current ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
        </div>
      )}
      {briefError && (
        <p className="text-meta text-red-500 mt-2">{briefError}</p>
      )}
    </Section>
  );
}

// ── Digest Configuration ──────────────────────────────────────────────────────

function DigestConfig({ project, canEdit }: { project: Project; canEdit: boolean }) {
  return (
    <Section
      title="Digest Configuration"
      description="How risk assessments and briefs are assembled for this project."
    >
      <div className="space-y-4 text-meta">
        <div className="flex items-center justify-between py-2 border-b border-gray-100 dark:border-white/5">
          <span className="text-gray-600 dark:text-gray-400">Brief Schedule</span>
          <span className="font-medium text-gray-800 dark:text-gray-200">
            {project.morningBriefEnabled ? '9 AM Company Timezone' : 'Disabled'}
          </span>
        </div>
        <div className="flex items-center justify-between py-2 border-b border-gray-100 dark:border-white/5">
          <span className="text-gray-600 dark:text-gray-400">Detail Level</span>
          <span className="font-medium text-gray-800 dark:text-gray-200 capitalize">
            {project.briefDetailLevel ?? 'Summary'}
          </span>
        </div>
        <div className="flex items-center justify-between py-2">
          <span className="text-gray-600 dark:text-gray-400">Risk Assessment</span>
          <span className="font-medium text-gray-800 dark:text-gray-200">Configured in Company Settings</span>
        </div>
      </div>
    </Section>
  );
}

// ── Danger Zone ───────────────────────────────────────────────────────────────

function DangerZone({ canRefresh, canDelete, isRefreshing, onRefresh, onDeleteRequest }: {
  canRefresh: boolean;
  canDelete: boolean;
  isRefreshing: boolean;
  onRefresh: () => void;
  onDeleteRequest: () => void;
}) {
  if (!canRefresh && !canDelete) return null;

  return (
    <Section title="Actions">
      <div className="space-y-4">
        {canRefresh && (
          <div className="flex items-center justify-between py-2 border-b border-gray-100 dark:border-white/5">
            <div>
              <p className="text-body font-medium text-gray-700 dark:text-gray-300">Refresh Risk Assessment</p>
              <p className="text-meta text-gray-400">Immediately re-evaluate project risk</p>
            </div>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={onRefresh}
              disabled={isRefreshing}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-primary/10 text-primary border border-primary/20 text-meta font-medium hover:bg-primary/20 transition-colors disabled:opacity-50"
            >
              <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
              {isRefreshing ? 'Running…' : 'Refresh'}
            </motion.button>
          </div>
        )}
        {canDelete && (
          <div className="flex items-center justify-between py-2">
            <div>
              <p className="text-body font-medium text-red-600 dark:text-red-400">Delete Project</p>
              <p className="text-meta text-gray-400">Permanently delete this project and all its data</p>
            </div>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={onDeleteRequest}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-red-50 dark:bg-red-900/10 text-red-600 dark:text-red-400 border border-red-200 dark:border-red-900/20 text-meta font-medium hover:bg-red-100 dark:hover:bg-red-900/20 transition-colors"
            >
              <Trash2 className="h-4 w-4" />
              Delete
            </motion.button>
          </div>
        )}
      </div>
    </Section>
  );
}

// ── People Settings ───────────────────────────────────────────────────────────

const MEMBER_ROLE_COLORS: Record<string, string> = {
  owner: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
  contributor: 'bg-gray-100 text-gray-600 dark:bg-white/10 dark:text-gray-300',
  suggested: 'border border-dashed border-gray-300 dark:border-white/20 text-gray-500 dark:text-gray-400',
};

function PeopleSettings({ project, canEdit }: { project: Project; canEdit: boolean }) {
  const qc = useQueryClient();
  const membersQueryKey = ['project-members', project.projectId];
  const usersQueryKey = ['entity-users'];

  const { data: members = [], isLoading: membersLoading } = useQuery<ProjectMember[]>({
    queryKey: membersQueryKey,
    queryFn: () => getProjectMembers(project.projectId),
  });

  const { data: allUsers = [] } = useQuery<EntityUser[]>({
    queryKey: usersQueryKey,
    queryFn: () => listUsers(),
  });

  // Team Lead
  const [teamLeadId, setTeamLeadId] = useState<string>(project.teamLeadId ?? '');
  const [teamLeadSaving, setTeamLeadSaving] = useState(false);

  // Visibility
  const [visibility, setVisibility] = useState<'team' | 'company'>(project.visibility ?? 'team');
  const [execAccess, setExecAccess] = useState<'all' | 'selected'>(project.executiveAccess ?? 'all');
  const [execIds, setExecIds] = useState<string[]>(project.executiveIds ?? []);
  const [visibilitySaving, setVisibilitySaving] = useState(false);
  const [visibilityDirty, setVisibilityDirty] = useState(false);

  // Add member UI
  const [addUserId, setAddUserId] = useState('');
  const [addRole, setAddRole] = useState<'owner' | 'contributor' | 'suggested'>('contributor');
  const [addError, setAddError] = useState<string | null>(null);

  const memberUserIds = new Set(members.map((m) => m.userId));
  const availableToAdd = allUsers.filter((u) => !memberUserIds.has(u.userId));
  const executiveUsers = allUsers.filter(
    (u) => u.functionalRoles?.includes('executive') || u.role === 'executive'
  );

  const handleTeamLeadChange = async (id: string) => {
    setTeamLeadId(id);
    setTeamLeadSaving(true);
    try {
      await updateProject(project.projectId, { teamLeadId: id || null });
      void qc.invalidateQueries({ queryKey: queryKeys.project(project.projectId) });
    } finally {
      setTeamLeadSaving(false);
    }
  };

  const handleVisibilitySave = async () => {
    setVisibilitySaving(true);
    try {
      await updateProject(project.projectId, {
        visibility,
        executiveAccess: execAccess,
        executiveIds: execAccess === 'selected' ? execIds : [],
      });
      void qc.invalidateQueries({ queryKey: queryKeys.project(project.projectId) });
      setVisibilityDirty(false);
    } finally {
      setVisibilitySaving(false);
    }
  };

  const addMemberMutation = useMutation({
    mutationFn: () => addProjectMember(project.projectId, addUserId, addRole),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: membersQueryKey });
      setAddUserId('');
      setAddRole('contributor');
      setAddError(null);
    },
    onError: (err: Error) => {
      setAddError(err.message ?? 'Failed to add member.');
    },
  });

  const [confirmRemove, setConfirmRemove] = useState<{ userId: string; name: string } | null>(null);

  const removeMemberMutation = useMutation({
    mutationFn: (userId: string) => removeProjectMember(project.projectId, userId),
    onSuccess: () => {
      setConfirmRemove(null);
      void qc.invalidateQueries({ queryKey: membersQueryKey });
    },
  });

  const teamLeadUser = allUsers.find((u) => u.userId === teamLeadId);

  return (
    <Section
      title="People"
      description="Team lead, project visibility, and member access."
    >
      <div className="space-y-6">
        {/* Team Lead */}
        <div>
          <label className="block text-meta font-medium text-gray-600 dark:text-gray-400 mb-1.5">
            Team Lead
          </label>
          <div className="flex items-center gap-2">
            <select
              value={teamLeadId}
              onChange={(e) => void handleTeamLeadChange(e.target.value)}
              disabled={teamLeadSaving || !canEdit}
              className="flex-1 px-3 py-2 rounded-lg bg-white dark:bg-gray-900 border border-gray-200 dark:border-white/10 text-gray-900 dark:text-white text-meta focus:outline-none focus:border-primary/50 disabled:opacity-50"
            >
              <option value="">— No team lead —</option>
              {allUsers.map((u) => (
                <option key={u.userId} value={u.userId}>
                  {u.fullName || u.email}
                </option>
              ))}
            </select>
            {teamLeadSaving && <Loader2 className="h-4 w-4 animate-spin text-gray-400" />}
          </div>
          {teamLeadUser && (
            <p className="text-meta text-gray-400 mt-1">
              Currently: {teamLeadUser.fullName || teamLeadUser.email}
            </p>
          )}
        </div>

        {/* Visibility */}
        <div>
          <label className="block text-meta font-medium text-gray-600 dark:text-gray-400 mb-1.5">
            Visibility
          </label>
          <div className="flex gap-2">
            {(['team', 'company'] as const).map((v) => (
              <button
                key={v}
                onClick={() => { if (canEdit) { setVisibility(v); setVisibilityDirty(true); } }}
                className={`px-3 py-1.5 rounded-lg text-meta font-medium border transition-colors ${
                  visibility === v
                    ? 'bg-primary text-white border-primary'
                    : 'bg-white dark:bg-white/5 text-gray-600 dark:text-gray-300 border-gray-200 dark:border-white/10 hover:border-primary/40'
                }`}
              >
                {v === 'team' ? 'Team only' : 'Whole company'}
              </button>
            ))}
          </div>
          <p className="text-meta text-gray-400 mt-1">
            {visibility === 'company'
              ? 'All members of the organization can observe this project.'
              : 'Only people with an involvement record can see this project.'}
          </p>
        </div>

        {/* Executive Access — only when visibility is 'team' */}
        {visibility === 'team' && (
          <div>
            <label className="block text-meta font-medium text-gray-600 dark:text-gray-400 mb-1.5">
              Executive Access
            </label>
            <div className="flex gap-2 mb-2">
              {(['all', 'selected'] as const).map((v) => (
                <button
                  key={v}
                  onClick={() => { if (canEdit) { setExecAccess(v); setVisibilityDirty(true); } }}
                  className={`px-3 py-1.5 rounded-lg text-meta font-medium border transition-colors ${
                    execAccess === v
                      ? 'bg-primary text-white border-primary'
                      : 'bg-white dark:bg-white/5 text-gray-600 dark:text-gray-300 border-gray-200 dark:border-white/10 hover:border-primary/40'
                  }`}
                >
                  {v === 'all' ? 'All executives' : 'Selected only'}
                </button>
              ))}
            </div>
            {execAccess === 'selected' && (
              <div className="space-y-1">
                {executiveUsers.length === 0 && (
                  <p className="text-meta text-gray-400">No executive users found.</p>
                )}
                {executiveUsers.map((u) => (
                  <label key={u.userId} className="flex items-center gap-2 text-meta text-gray-700 dark:text-gray-300 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={execIds.includes(u.userId)}
                      onChange={(e) => {
                        setExecIds(e.target.checked
                          ? [...execIds, u.userId]
                          : execIds.filter((id) => id !== u.userId));
                        setVisibilityDirty(true);
                      }}
                      className="rounded border-gray-300 dark:border-white/20"
                    />
                    {u.fullName || u.email}
                  </label>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Visibility save button */}
        {visibilityDirty && (
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => void handleVisibilitySave()}
            disabled={visibilitySaving}
            className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-xl text-meta font-medium hover:bg-primary/90 transition-colors disabled:opacity-50"
          >
            {visibilitySaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            {visibilitySaving ? 'Saving…' : 'Save'}
          </motion.button>
        )}

        {/* Divider */}
        <div className="border-t border-gray-100 dark:border-white/[0.06]" />

        {/* Project Members */}
        <div>
          <h4 className="text-meta font-medium text-gray-600 dark:text-gray-400 mb-2">Project Members</h4>
          {membersLoading ? (
            <p className="text-meta text-gray-400">Loading…</p>
          ) : (
            <div className="space-y-1.5">
              {members.map((m: ProjectMember) => {
                const user = allUsers.find((u) => u.userId === m.userId);
                return (
                  <div
                    key={m.involvementId}
                    className="flex items-center justify-between gap-2 px-3 py-2 rounded-lg bg-gray-50 dark:bg-white/[0.03] border border-gray-100 dark:border-white/[0.06]"
                  >
                    <span className="text-body text-gray-800 dark:text-gray-200 truncate flex-1">
                      {user ? (user.fullName || user.email) : m.userId}
                    </span>
                    <span className={`text-meta font-medium px-2 py-0.5 rounded-full ${MEMBER_ROLE_COLORS[m.role] ?? MEMBER_ROLE_COLORS.contributor}`}>
                      {m.role}
                    </span>
                    <button
                      onClick={() => setConfirmRemove({ userId: m.userId, name: user ? (user.fullName || user.email) : m.userId })}
                      disabled={removeMemberMutation.isPending || !canEdit}
                      className="text-gray-400 hover:text-red-500 transition-colors disabled:opacity-40 flex-shrink-0 focus-visible:ring-2 focus-visible:ring-primary/50 rounded"
                      title="Remove member"
                      aria-label={`Remove ${user ? (user.fullName || user.email) : m.userId} from project`}
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                );
              })}
              {members.length === 0 && (
                <p className="text-meta text-gray-400">No members yet.</p>
              )}
            </div>
          )}

          {/* Add member */}
          <div className="mt-3 flex gap-2 items-center flex-wrap">
            <select
              value={addUserId}
              onChange={(e) => setAddUserId(e.target.value)}
              className="flex-1 min-w-0 px-3 py-1.5 rounded-lg bg-white dark:bg-gray-900 border border-gray-200 dark:border-white/10 text-gray-900 dark:text-white text-meta focus:outline-none focus:border-primary/50"
            >
              <option value="">— Select person —</option>
              {availableToAdd.map((u) => (
                <option key={u.userId} value={u.userId}>
                  {u.fullName || u.email}
                </option>
              ))}
            </select>
            <select
              value={addRole}
              onChange={(e) => setAddRole(e.target.value as 'owner' | 'contributor' | 'suggested')}
              className="px-3 py-1.5 rounded-lg bg-white dark:bg-gray-900 border border-gray-200 dark:border-white/10 text-gray-900 dark:text-white text-meta focus:outline-none focus:border-primary/50"
            >
              <option value="owner">Owner</option>
              <option value="contributor">Contributor</option>
              <option value="suggested">Suggested</option>
            </select>
            <button
              onClick={() => { if (addUserId && canEdit) addMemberMutation.mutate(); }}
              disabled={!addUserId || addMemberMutation.isPending || !canEdit}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-primary/10 text-primary border border-primary/20 text-meta font-medium hover:bg-primary/20 transition-colors disabled:opacity-40"
            >
              {addMemberMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
              Add
            </button>
          </div>
          {addError && <p className="text-meta text-red-500 mt-1">{addError}</p>}
        </div>

        {confirmRemove && (
          <ConfirmModal
            title="Remove member?"
            body={`${confirmRemove.name} will lose access to this project. Their past activity is kept; you can add them back at any time.`}
            confirmLabel="Remove Member"
            confirmClassName="bg-red-600 hover:bg-red-700 text-white"
            onConfirm={() => removeMemberMutation.mutate(confirmRemove.userId)}
            onCancel={() => setConfirmRemove(null)}
            loading={removeMemberMutation.isPending}
          />
        )}
      </div>
    </Section>
  );
}

// ── Main export ───────────────────────────────────────────────────────────────

export function ProjectSettingsPanel({
  project,
  sessionConfig,
  canRefresh,
  canDelete,
  isRefreshing,
  onRefresh,
  onDeleteRequest,
}: ProjectSettingsPanelProps) {
  const canEditBrief =
    sessionConfig?.entityRole === 'admin' ||
    sessionConfig?.role === 'admin' ||
    sessionConfig?.role === 'product_manager' ||
    sessionConfig?.functionalRoles?.includes('product_manager') ||
    false;

  useEffect(() => {
    track('project.settings.opened', 'feature_use', { projectId: project.projectId });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project.projectId]);

  const canEditPeople =
    sessionConfig?.entityRole === 'admin' ||
    sessionConfig?.role === 'admin' ||
    sessionConfig?.role === 'product_manager' ||
    sessionConfig?.functionalRoles?.includes('product_manager') ||
    false;

  // Two balanced column stacks instead of the old max-w-3xl single column
  // (which left ~two thirds of a wide viewport empty). The shell stays
  // uncapped per the app-wide container model; the stacks split at xl.
  return (
    <div className="px-6 sm:px-10 lg:px-16 pb-6 pt-4 grid grid-cols-1 xl:grid-cols-2 gap-5 items-start">
      <div className="space-y-5 min-w-0">
        <PeopleSettings project={project} canEdit={canEditPeople} />
        <DangerZone
          canRefresh={canRefresh}
          canDelete={canDelete}
          isRefreshing={isRefreshing}
          onRefresh={onRefresh}
          onDeleteRequest={() => {
            track('project.delete_initiated', 'action', { projectId: project.projectId });
            onDeleteRequest();
          }}
        />
      </div>
      <div className="space-y-5 min-w-0">
        <ChannelBriefSettings project={project} canEdit={canEditBrief} />
        <PersonalBriefSettings project={project} />
        <DigestConfig project={project} canEdit={canEditBrief} />
      </div>
    </div>
  );
}
